import qs from 'qs';
import { format } from 'date-fns';

import { transformKeysToSnakeCase } from 'helpers/core';
import { makePath } from 'helpers/link-generation';

import { SERVER_DATE_FORMAT } from './logic';

export default {
  user: {
    decagonAuthToken: '/api/partners/v1/decagon/auth/token',
    decagonAuthTokenPublic: '/api/partners/public/v1/decagon/auth/token/anonymous',
    decagonPartnersApiAuthToken: '/api/partners/v1/decagon/auth/partners-decagon-jwt',
    info: '/api/v1/users/info',
    logIn: '/api/v1/login',
    changePassword: '/api/v1/changepassword',
    forgotPassword: '/api/v1/resetPassword',
    resetPassword: resetToken => `/api/v1/updatePassword/${resetToken}`,
    logOut: '/api/v1/logout',
    updateLocale: '/api/v1/locale',
    settings: '/api/v1/settings',
    smsSubscriptionStatus: `/api/partners/v1/dashboard_users/sms/status`,
    updateSmsSubscriptionStatus: userId => `/api/partners/v1/dashboard_users/${userId}/sms/update`,
    marketingSubscription: '/api/partners/v1/dashboard_users/marketing_subscription',
    initiateConversation: venueId => `/api/partners/v1/${venueId}/conversation`,
    getConversationStatus: (venueId, reservationId) =>
      `/api/partners/v1/${venueId}/conversation/reservation/${reservationId}`,
    getConversationLimit: '/api/partners/v1/dashboard_users/conversation/status',
    getEmailBounceStatus: venueId => `/api/partners/v1/${venueId}/dashboard_users/email/status`,
    mfa: {
      checkMFAStatus: '/api/partners/v1/dashboard_users/mfa/status',
      disableMFA: '/api/partners/v1/dashboard_users/mfa/disable',
      enableMFA: '/api/partners/v1/dashboard_users/mfa/setup',
      generateRecoveryCodes: '/api/partners/v1/dashboard_users/mfa/recovery_codes',
      recoverCode: 'api/v1/mfa/recover',
      verifyOTP: '/api/partners/v1/dashboard_users/mfa/verify_setup',
      verifyMFAForLogin: '/api/v1/mfa/verify',
    },
  },
  venue: {
    appointmentAlternativeTime: (venueId, reservationId) =>
      `/api/partners/v1/venues/${venueId}/appointment/${reservationId}/alternate_times`,
    calendarAvailability: ({ venueId, startDate, endDate }) =>
      `/api/partners/v1/venues/${venueId}/appointment_availability?start_date=${startDate}&end_date=${endDate}`,
    calendarConfiguration: venueId => `/api/partners/v1/venue/${venueId}/calendar_configuration`,
    cancelReservation: `/api/partners/v1/reservations/cancel`,
    carouselPhotos: venueId => `/api/partners/v1/${venueId}/profile_management/carousel_photos`,
    carouselPhotosDelete: (venueId, photoId) =>
      `/api/partners/v1/${venueId}/profile_management/carousel_photos/${photoId}`,
    classes: venueId => `/api/partners/v1/venue/${venueId}/classes`,
    classPricing: (venueId, isOnlyActive = true) =>
      `/api/partners/v1/venues/${venueId}/pricing-rules/classes?only_active=${isOnlyActive}`,
    classtivitySchedules: (venueId, startDate, endDate) =>
      `/api/partners/v1/venues/${venueId}/schedules/from/${startDate}/to/${endDate}`,
    createClass: venueId => `api/v1/${venueId}/class/create`,
    createClasstivitySchedules: venueId => `/api/v1/v3/${venueId}/schedule/create`,
    createDisabledTimeWindow: ({ venueId }) => `api/partners/v1/venue/${venueId}/inventory_disabled_time_windows`,
    createWaiver: venueId => `/api/partners/v1/venues/${venueId}/waivers`,
    deleteDisabledTimeWindow: ({ venueId, timeWindowId }) =>
      `api/partners/v1/venue/${venueId}/inventory_disabled_time_windows/${timeWindowId}`,
    details: venueId => `/api/partners/v1/venues/${venueId}`,
    fcf: {
      getSettings: venueId => `/api/partners/v1/venues/${venueId}/fcf/settings`,
      updateSettings: venueId => `/api/partners/v1/venues/${venueId}/fcf/settings`,
      bulkUpdateSettings: '/api/partners/v1/venues/fcf-settings/bulk',
    },
    getDisabledTimeWindows: ({ venueId, endsAfter }) => {
      const query = qs.stringify(transformKeysToSnakeCase({ endsAfter }));
      return `api/partners/v1/venue/${venueId}/inventory_disabled_time_windows?${query}`;
    },
    googleAuth: venueId => {
      const finalDestination = window.location.origin + makePath.calendarLinks(venueId);
      const query = qs.stringify(transformKeysToSnakeCase({ finalDestination }));
      return `/api/partners/v1/venue/${venueId}/gcal/auth?${query}`;
    },
    googleCalendarsList: venueId => `/api/partners/v1/venue/${venueId}/gcal/calendars`,
    googleDeleteAuth: venueId => `/api/partners/v1/venue/${venueId}/gcal/auth`,
    instructors: (venueId, entityType) => {
      const query = entityType ? qs.stringify(transformKeysToSnakeCase({ entityType })) : '';
      return `/api/partners/v1/venue/${venueId}/instructors?${query}`;
    },
    opportunities: (venueId, includeClassLevel = false) =>
      `/api/v1/${venueId}/opportunities?show_disabled_classes=${includeClassLevel}`,
    opportunitiesCount: (venueId, classIds = []) => {
      const query = classIds.length ? `?${qs.stringify({ class_ids: classIds }, { arrayFormat: 'repeat' })}` : '';
      return `/api/partners/v1/${venueId}/opportunities/count${query}`;
    },
    opportunitiesAct: venueId => `/api/partners/v1/${venueId}/opportunities/act`,
    opportunitiesDisplay: venueId => `/api/partners/v1/${venueId}/opportunities/show`,
    opportunitiesReject: venueId => `/api/partners/v1/${venueId}/opportunities/reject`,
    photos: venueId => `/api/partners/v1/${venueId}/profile_management/photos`,
    pricingControls: {
      timeExceptions: '/api/v1/{venueId}/pricing_controls',
      classFloors: '/api/v1/{venueId}/pricing_controls/class/list',
    },
    refresh: venueId => `/api/partners/v1/venues/refresh/${venueId}`,
    reservations: ({ venueId, startDate, endDate }) => {
      const query = qs.stringify(transformKeysToSnakeCase({ startDate, endDate }));
      return `api/partners/v1/venues/${venueId}/reservations?${query}`;
    },
    roster: (venueId, scheduleId) => `/api/partners/v1/venues/${venueId}/schedules/${scheduleId}/roster`,
    schedule: (venueId, scheduleId) => `/api/partners/v2/venues/${venueId}/schedules/${scheduleId}`,
    schedules: (venueId, date, page, entityType) => {
      const entityQuery = entityType ? `&entity=${entityType}` : '';
      return `/api/partners/v2/venues/${venueId}/schedules?date=${date}&page=${page}${entityQuery}`;
    },
    smartspotData: '/api/v1/{venueId}/optimize',
    smartSpotFillChartData: venueId => `/api/partners/v1/smartspot/${venueId}/performance`,
    smartSpotOpportunities: venueId => `/api/partners/v1/${venueId}/smartspot-opportunities`,
    smartSpotOpportunitiesBatchUpdate: venueId => `/api/partners/v2/venues/${venueId}/schedules/spots`,
    taxCategory: '/api/partners/v1/venues/tax_category',
    updateCarouselPhotosOrder: venueId => `api/partners/v1/${venueId}/profile_management/carousel_photos/update_order`,
    updateWaiver: (venueId, waiverId) => `/api/partners/v1/venues/${venueId}/waivers/${waiverId}`,
    venueInsights: venueId => `/api/partners/v1/venues/${venueId}/insights`,
    venuesDynamicPricing: '/api/partners/v1/venues/dynamic-pricing',
    videos: {
      create: venueId => `/api/partners/v1/venues/${venueId}/videos`,
      delete: (venueId, videoId) => `/api/partners/v1/venues/${venueId}/videos/${videoId}`,
      read: venueId => `/api/partners/v1/venues/${venueId}/videos`,
      update: (venueId, videoId) => `/api/partners/v1/venues/${venueId}/videos/${videoId}`,
    },
    waiver: venueId => `/api/partners/v1/venues/${venueId}/waivers/latest`,
    waiverVersion: (venueId, waiverId, waiverVersionId) =>
      `/api/partners/v1/venues/${venueId}/waivers/${waiverId}/versions/${waiverVersionId}`,
  },
  schedule: {
    cancel: scheduleId => `/api/partners/v1/classes/${scheduleId}/cancel`,
    enable: (venueId, scheduleId) => `/api/v1/${venueId}/schedule/${scheduleId}/enable`,
    updateSmartSpot: '/api/v1/{venueId}/optimizeSchedule/{scheduleId}',
    updateReservationAttendance: (venueId, reservationId) =>
      `/api/partners/v1/venues/${venueId}/reservations/${reservationId}/attendance`,
    updateReservationFeedback: '/api/partners/v1/reservations/{reservationId}/feedback',
    submitReservationFeedback: '/api/partners/v2/reservations/feedback',
    print: (venueId, scheduleId, startDate) =>
      `/venues/${venueId}/schedules/${scheduleId}/roster/print?startDate=${startDate}`,
    insights: (venueId, scheduleId, historyCount) =>
      `api/partners/v1/venues/${venueId}/schedules/${scheduleId}/insights?history_count=${historyCount}`,
    setFull: (venueId, scheduleId) => `/api/v1/${venueId}/schedule/${scheduleId}/set_full`,
    updateSpots: (venueId, scheduleId) => `/api/partners/v2/venues/${venueId}/schedules/${scheduleId}/spots`,
    notifyScheduleUsers: (venueId, scheduleId) =>
      `/api/partners/v1/venues/${venueId}/schedules/${scheduleId}/notify_all`,
    notifyUsers: venueId => `/api/partners/v1/venues/${venueId}/message_members`,
  },
  class: {
    update: classId => `/api/partners/v1/classes/${classId}`,
    detailsRead: classId => `/api/partners/v1/classes/${classId}`,
    tags: ({ excludeOutdoors = false }) => {
      const query = qs.stringify(
        transformKeysToSnakeCase({
          excludeOutdoors,
        })
      );
      return `/api/partners/v1/tags?${query}`;
    },
    overrides: (venueId, classId) => `/api/partners/v1/venues/${venueId}/classes/${classId}/overrides`,
    reservations: (venueId, classId) => `/api/partners/v1/venues/${venueId}/class/${classId}/reservations`,
  },
  connect: {
    credentials: 'api/partners/public/v1/connect/credentials',
  },
  home: {
    recommendations: (userId, venueId) =>
      `/api/partners/v1/dashboard_users/${userId}/venues/${venueId}/recommendations`,
    upcomingBookings: (venueId, startDate, endDate) => {
      const query = qs.stringify(
        transformKeysToSnakeCase({
          startDate: format(startDate, SERVER_DATE_FORMAT),
          endDate: format(endDate, SERVER_DATE_FORMAT),
        })
      );
      return `/api/partners/v1/venues/${venueId}/total_bookings_by_day?${query}`;
    },
    upcomingSpots: (venueId, startDate, endDate) => {
      const query = qs.stringify(
        transformKeysToSnakeCase({
          startDate: format(startDate, SERVER_DATE_FORMAT),
          endDate: format(endDate, SERVER_DATE_FORMAT),
        })
      );
      return `/api/partners/v1/venues/${venueId}/total_spots_by_day?${query}`;
    },
    userInsights: venueId => `/api/v1/${venueId}/user_insights_overview_card`,
    userInsightsGraph: venueId => `/api/partners/v1/venues/${venueId}/user_insights_graph_data`,
    excessInventory: (venueId, weeksCount, scheduleId) => {
      const query = qs.stringify(
        transformKeysToSnakeCase({
          weeksCount,
          scheduleId,
        })
      );

      return `/api/partners/v1/stats/insights/venues/${venueId}/spots?${query}`;
    },
    attendanceInquiries: (venueId, inquiryId, status) => {
      const params = {};
      if (inquiryId) params.first_inquiry = inquiryId;
      if (status) params.status = status;
      const query = Object.keys(params).length > 0 ? `?${qs.stringify(params)}` : '';
      return `/api/partners/v1/attendance_inquiries/venue/${venueId}${query}`;
    },
    respondAttendanceInquiriesBulk: () => '/api/partners/v1/attendance_inquiry/respond-bulk',
  },
  instructors: {
    list: venueId => `/ms-inventory/${venueId}/api/v1/instructors?page=1&count=5000`,
    instructor: (venueId, instructorId) => `/ms-inventory/${venueId}/api/v1/instructors/${instructorId}`,
    add: venueId => `/ms-inventory/${venueId}/api/v1/instructors`,
    edit: (venueId, instructorId) => `/ms-inventory/${venueId}/api/v1/instructors/${instructorId}`,
    delete: (venueId, instructorId) => `/ms-inventory/${venueId}/api/v1/instructors/${instructorId}`,
  },
  integrators: {
    detailList: '/api/partners/v1/integrators',
    requestToIntegrate: venueId => `/api/partners/v1/integrators/venues/${venueId}/request-to-integrate`,
  },
  msSchedules: {
    scheduleInstancesByDay: (venueId, filters, pageDate) => {
      const perPage = 50;
      let query = filters.map(filter => qs.stringify({ filtering: filter }));
      query = [...query, `start_date=${format(pageDate, SERVER_DATE_FORMAT)}`, `desired_count=${perPage}`];
      query = query.join('&');

      return `/ms-inventory/${venueId}/api/v1/schedule_instances/by_day?${query}`;
    },
    checkTerminationType: (venueId, scheduleId) =>
      `/ms-inventory/${venueId}/api/v1/schedules/${scheduleId}/check_termination_type`,
    create: venueId => `/ms-inventory/${venueId}/api/v1/multitime-schedules`,
    get: (venueId, id) => `/ms-inventory/${venueId}/api/v1/schedules/${id}`,
    update: (venueId, id) => `/ms-inventory/${venueId}/api/v1/schedules/${id}`,
    classes: (venueId, count = 1000) => {
      const queryParams = { count };
      const query = qs.stringify(queryParams);
      return `/ms-inventory/${venueId}/api/v1/classes?${query}`;
    },
    createClass: venueId => `/ms-inventory/${venueId}/api/v2/classes`,
    updateClass: (venueId, classId) => `/ms-inventory/${venueId}/api/v1/classes/${classId}`,
    delete: (venueId, scheduleId) => `/ms-inventory/${venueId}/api/v1/schedules/${scheduleId}`,
    getParentMSIScheduleId: (venueId, apiGuid) =>
      `/ms-inventory/${venueId}/api/v1/schedule_instances/by_external_id/${apiGuid}/get_msi_schedule_id`,
    cancelOccurrence: (venueId, scheduleId, occurrenceDate) =>
      `/ms-inventory/${venueId}/api/v1/schedules/${scheduleId}/cancel_occurrence?occurrence_date=${occurrenceDate}`,
    updateOccurrence: (venueId, scheduleId, occurrenceDate) =>
      `/ms-inventory/${venueId}/api/v1/schedules/${scheduleId}/modify_occurrence?occurrence_date=${occurrenceDate}`,
    singleScheduleOccurrence: (venueId, scheduleId, occurrenceDate) =>
      `/ms-inventory/${venueId}/api/v1/schedules/${scheduleId}/occurrence?occurrence_date=${occurrenceDate}`,
  },
  reports: {
    downloadMonthlyReport: (venueId, year, month) => {
      const twoDigitMonth = `0${month}`.slice(-2);
      return `reports/${venueId}/monthly/${year}/${twoDigitMonth}`;
    },
    downloadReservationsCSV: '/api/partners/v1/reports/reservations/csv',
    campaigns: query =>
      `/api/partners/v2/reports/campaigns/reservations?${qs.stringify(transformKeysToSnakeCase(query))}`,
    reservations: '/api/partners/v1/reports/reservations',
    schedule: scheduleId => `/api/partners/v1/reports/schedule?schedule_id=${scheduleId}`,
    schedules: `/api/partners/v1/reports/classes`,
    summary: queryParams => `/api/partners/v1/reports/summary?${qs.stringify(queryParams)}`,
    transactions: queryParams => `/api/partners/v1/reports/transactions?${qs.stringify(queryParams)}`,
    payouts: queryParams => `/api/partners/v1/reports/payouts?${qs.stringify(queryParams)}`,
    payoutDetails: ({ payoutId, venueId }) => `/api/partners/v1/${venueId}/reports/payout/${payoutId}`,
    payoutOrdersCsv: ({ payoutId, venueId }) =>
      `/api/partners/v1/reports/payout/${payoutId}/orders/csv?venue_id=${venueId}`,
    invoiceStatus: ({ payoutId, venueId }) =>
      `/api/partners/v1/reports/payout/invoice/${payoutId}/status?venue_id=${venueId}`,
    invoiceDownload: ({ payoutId, venueId }) =>
      `/api/partners/v1/reports/payout/invoice/${payoutId}/file?venue_id=${venueId}`,
  },
  smartSpot: {
    venueSettings: venueId => `/api/v1/smartspot/settings/venue/${venueId}`,
    recurringSettings: venueId => `/api/v1/smartspot/${venueId}/recurring_schedule_rule`,
    scheduleSettings: (venueId, scheduleId) => `/api/v1/smartspot/settings/venue/${venueId}/schedule/${scheduleId}`,
    scheduleDataByDate: (venueId, fromDate, toDate) => {
      const fromDateString = format(fromDate, SERVER_DATE_FORMAT);
      const toDateString = !toDate ? fromDateString : format(toDate, SERVER_DATE_FORMAT);
      return `/api/v1/smartspot/${venueId}/schedules/from/${fromDateString}/to/${toDateString}`;
    },
    reset: venueId => `/api/v1/smartspot/settings/venue/${venueId}/recurring_schedule_rule/all`,
    classLevelMinimumSpots: venueId => `/api/v1/smartspot/settings/venue/${venueId}/classes`,
  },
  smartRate: {
    faqs: '/api/partners/v1/smartrate/faqs',
    totalEarnings: (venueId, classIds = []) => {
      const query = classIds.length ? `?${qs.stringify({ class_ids: classIds }, { arrayFormat: 'repeat' })}` : '';
      return `/api/partners/v1/smartrate/venues/${venueId}/insights/total-earnings${query}`;
    },
    averagePayoutRate: (venueId, classIds = []) => {
      const query = classIds.length ? `?${qs.stringify({ class_ids: classIds }, { arrayFormat: 'repeat' })}` : '';
      return `/api/partners/v1/smartrate/venues/${venueId}/insights/average-payout-rate${query}`;
    },
  },
  ratings: {
    classesRatings: (venueId, instructorId) => {
      const query = !instructorId
        ? ''
        : qs.stringify(
            transformKeysToSnakeCase({
              teacherId: instructorId,
            })
          );
      return `/ratings-and-reviews/v2/venues/${venueId}/ratings/statistics/classes?${query}`;
    },
    instructorsRatings: (venueId, classId) => {
      const query = !classId ? '' : qs.stringify(transformKeysToSnakeCase({ classId }));
      return `/ratings-and-reviews/v2/venues/${venueId}/ratings/statistics/teachers?${query}`;
    },
    reviewsList: (venueId, page, { classId, instructorId, userFilterKey }) => {
      const query = qs.stringify(
        transformKeysToSnakeCase({
          page,
          pageSize: 30,
          classId,
          teacherId: instructorId,
          users: userFilterKey,
        })
      );
      return `/ratings-and-reviews/v2/venues/${venueId}/reviews?${query}`;
    },
    reportReview: (venueId, reviewId) => `/ratings-and-reviews/v2/venues/${venueId}/reviews/${reviewId}/abuse_report`,
    removeResponse: (venueId, reviewId) => `/ratings-and-reviews/v2/venues/${venueId}/reviews/${reviewId}/response`,
    respond: (venueId, reviewId) => `/ratings-and-reviews/v2/venues/${venueId}/reviews/${reviewId}/response`,
    statistics: (venueId, { classId, instructorId, userFilterKey }) => {
      const query = qs.stringify(
        transformKeysToSnakeCase({
          classId,
          teacherId: instructorId,
          users: userFilterKey,
        })
      );
      return `/ratings-and-reviews/v2/venues/${venueId}/ratings/statistics?${query}`;
    },
    instructors: venueId => `/ratings-and-reviews/v2/venues/${venueId}/teachers`,
    classes: venueId => `/ratings-and-reviews/v2/venues/${venueId}/classes`,
  },
  staffAccounts: {
    get: '/api/v1/dashboard_users',
    create: '/api/v1/dashboard_users',
    update: userId => `/api/v1/dashboard_users/${userId}`,
  },
  stats: {
    classInsights: {
      availableClasses: (venueId, startDate, endDate) => {
        const query = qs.stringify(
          transformKeysToSnakeCase({
            startDate: format(startDate, SERVER_DATE_FORMAT),
            endDate: format(endDate, SERVER_DATE_FORMAT),
          })
        );
        return `/api/partners/v1/stats/insights/venues/${venueId}/available_classes?${query}`;
      },
      utilizationByClassType: (venueId, startDate, endDate, classTimeFilter = null) => {
        const filters = {
          startDate: format(startDate, SERVER_DATE_FORMAT),
          endDate: format(endDate, SERVER_DATE_FORMAT),
        };

        if (classTimeFilter !== null) filters.classTimeFilter = classTimeFilter;

        const query = qs.stringify(transformKeysToSnakeCase(filters));

        return `/api/partners/v1/stats/insights/venues/${venueId}/class_utilization_by_type?${query}`;
      },
      utilizationByClassTime: (venueId, startDate, endDate, selectedClassesIds) => {
        const query = qs.stringify(
          transformKeysToSnakeCase({
            startDate: format(startDate, SERVER_DATE_FORMAT),
            endDate: format(endDate, SERVER_DATE_FORMAT),
            classIds: selectedClassesIds,
          }),
          { arrayFormat: 'repeat' }
        );
        return `/api/partners/v1/stats/insights/venues/${venueId}/class_utilization_by_time?${query}`;
      },
    },
    marketInsightsModulesList: venueId => `/api/partners/v1/stats/market_insights/venues/${venueId}/modules`,
    insightsModule: (url, startDate, endDate) => {
      const query = qs.stringify(
        transformKeysToSnakeCase({
          startDate: format(startDate, SERVER_DATE_FORMAT),
          endDate: format(endDate, SERVER_DATE_FORMAT),
        })
      );
      return `${url}?${query}`;
    },
    yourInsightsModulesList: venueId => `/api/partners/v1/stats/insights/venues/${venueId}/modules`,
    reports: {
      reservations: `/api/partners/v1/stats/reports/reservations`,
      revenue: `/api/partners/v1/stats/reports/revenue`,
    },
  },
  cms: {
    getLegalContent: countrySlug => `/api/v1/content_management/legal_terms/${countrySlug}`,
  },
  pike13: {
    info: venueId => `api/v1/frontdeskhq/${venueId}/info`,
    migrate: venueId => `api/v1/frontdeskhq/${venueId}/migrate`,
    locations: (subdomain, apiKey) => `https://${subdomain}.pike13.com/api/v2/front/locations?client_id=${apiKey}`,
    services: (subdomain, apiKey) => `https://${subdomain}.pike13.com/api/v2/front/services?client_id=${apiKey}`,
    authorize: (path, apiKey) =>
      `https://pike13.com/oauth/authorize?client_id=${apiKey}&response_type=code&redirect_uri=${path}`,
    oAuthRedirect: () => `${window.location.origin}/onboarding/front-desk/success`,
  },
  manageServices: {
    resources: {
      list: (venueId, { page = 1, pageSize = 100 } = {}) =>
        `/ms-inventory/${venueId}/api/v1/resources?page=${page}&count=${pageSize}`,
      create: venueId => `/ms-inventory/${venueId}/api/v1/resources`,
      update: (venueId, resourceId) => `/ms-inventory/${venueId}/api/v1/resource/${resourceId}`,
      delete: (venueId, resourceId) => `/ms-inventory/${venueId}/api/v1/resource/${resourceId}`,
    },
    practitioners: {
      list: venueId => `/ms-inventory/${venueId}/api/v1/practitioners`,
      create: venueId => `/ms-inventory/${venueId}/api/v1/practitioners`,
      update: (venueId, practitionerId) => `/ms-inventory/${venueId}/api/v1/practitioners/${practitionerId}`,
      delete: (venueId, practitionerId) => `/ms-inventory/${venueId}/api/v1/practitioners/${practitionerId}`,
    },
    services: {
      create: venueId => `/ms-inventory/${venueId}/api/v1/services`,
      list: (venueId, { page = 1, pageSize = 100 } = {}) =>
        `/ms-inventory/${venueId}/api/v2/services?page=${page}&page_size=${pageSize}`,
      update: (venueId, serviceId) => `/ms-inventory/${venueId}/api/v1/service/${serviceId}`,
      delete: (venueId, serviceId) => `/ms-inventory/${venueId}/api/v1/service/${serviceId}`,
      submitRetailRateRequest: () => `/api/partners/v1/retail-rate-requests`,
      retailRateRequestsByVenue: (venueId, { msiUuid } = {}) =>
        `/api/partners/v1/retail-rate-requests/venue/${venueId}${
          msiUuid ? `?msi_uuid=${encodeURIComponent(msiUuid)}` : ''
        }`,
      retailRateRequestProof: venueId => `/api/partners/v1/retail-rate-requests/proof/venue/${venueId}`,
      integratedServiceById: (venueId, classId) => `/api/partners/v1/venue/${venueId}/integrated-services/${classId}`,
    },
    scheduleIncrements: {
      list: (venueId, serviceIds) =>
        `/ms-inventory/${venueId}/api/v2/service_schedule_increments?service_ids=${serviceIds}`,
      update: venueId => `/ms-inventory/${venueId}/api/v2/service_schedule_increments`,
    },
  },
  msInventory: {
    venues: venueId => `/ms-inventory/${venueId}/api/v1/venues`,
    changelog: (venueId, changelogType, page, pageSize) =>
      `/ms-inventory/${venueId}/api/v1/changelog/${changelogType}?page=${page}&count=${pageSize}`,
    detachService: (venueId, classId) =>
      `/ms-inventory/${venueId}/api/v2/service/by_openapi_id/${classId}/detach_practitioners`,
  },
  blockOffTime: (venueId, botId) => `/ms-inventory/${venueId}/api/v1/time_blocks${!botId ? '' : `/${botId}`}`,
  instructorCalendars: {
    list: venueId => `api/partners/v2/venue/${venueId}/teachers/calendar_urls`,
    update: venueId => `api/partners/v1/venue/${venueId}/calendar_urls`,
    delete: venueId => `api/partners/v1/venue/${venueId}/calendar_urls/delete`,
  },
  campaigns: {
    create: venueId => `/api/partners/v1/venues/${venueId}/campaign`,
    getOne: campaignId => `/api/partners/v1/venues/campaign/${campaignId}`,
    options: venueId => `/api/partners/v2/venues/${venueId}/campaigns/options`,
    read: venueId => `/api/partners/v2/venues/${venueId}/campaigns`,
    update: partnerCampaignId => `/api/partners/v1/venues/campaigns/${partnerCampaignId}`,
  },
  calendarConfigurationPlatforms: () => '/api/partners/v1/calendar_configuration_platforms',
  generateIcalExportKey: (entityId, entityType) => `api/partners/v1/calendar/${entityType}/${entityId}/export_url`,
  staffAndEquipment: {
    getServices: (venueId, staffId) => `/ms-inventory/${venueId}/api/v2/bookable/by_openapi_id/${staffId}`,
  },
  items: {
    serviceAndResource: venueId => `/api/partners/v1/items/venues/${venueId}/service-and-resource`,
    submitMenuPricing: venueId => `/api/partners/v1/items/venues/${venueId}/submit-menu-pricing`,
  },
  skuOrders: {
    getPartnerOrders: '/api/partners/v1/orders',
    fulfillOrder: orderId => `/api/partners/v1/orders/${orderId}/fulfill`,
    cancelOrder: orderId => `/api/partners/v1/orders/${orderId}/cancel`,
  },
  sku: {
    groups: venueId => `/api/partners/v1/venues/${venueId}/sku/groups`,
    group: (venueId, groupId) => `/api/partners/v1/venues/${venueId}/sku/groups/${groupId}`,
    subgroups: venueId => `/api/partners/v1/venues/${venueId}/sku/subgroups`,
    subgroup: (venueId, subgroupId) => `/api/partners/v1/venues/${venueId}/sku/subgroups/${subgroupId}`,
    items: venueId => `/api/partners/v1/venues/${venueId}/sku/items`,
    item: (venueId, itemId) => `/api/partners/v1/venues/${venueId}/sku/items/${itemId}`,
    messageMembers: venueId => `/api/partners/v1/venues/${venueId}/sku/message_members`,
    linkItem: (venueId, subgroupId, itemId) =>
      `/api/partners/v1/venues/${venueId}/sku/subgroups/${subgroupId}/items/${itemId}/link`,
  },
};
